import type { ApiColorLabel } from '@fabric-app/types/labels';
import type { ResourceDetail } from '@fabric-app/types/resources';
import { useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import useAuthStore from '@/src/hooks/auth';
import useOpenInTabs from '@/src/hooks/useOpenInTabs';
import { useMutationChangeResourceLabel } from '@fabric-app/business/resource-detail/mutations/useMutationChangeResourceLabel';
import { ContextMenu } from '@fabric-app/ui/components/ContextMenu/ContextMenu';
import LabelSelector from '@fabric-app/business/labels/components/LabelSelector/LabelSelector';
import { useContextShareToken } from '@fabric-app/business/permission/contexts/AnonShareTokenContext';
import { getMultipleResourcesLeaveOrDeleteActionDetails } from '@fabric-app/business/resource-detail/components/context/getDisabledActionsReasons';
import { ContextMenuCopyResources } from '@fabric-app/business/resources/components/ContextMenu/ContextMenuCopyResources';
import { DropdownItemIconContainer } from '@fabric-app/ui/components/DropdownMenu/Item';
import { IconAssistantFace } from '@fabric-app/icons/svg';
import { useAddToAssistantContextDisabledReason } from '@fabric-app/business/assistant/stores/assistantStore';

export const ResourceListContextMenuSelectedItemsContent: React.FC<{
  onAddSelectionToSpaceClick: (e?: React.MouseEvent) => void;
  onOpenSelectionAsTabsClick: (e?: React.MouseEvent) => void;
  onAskAssistantSelectionClick: (e?: React.MouseEvent, added?: boolean) => void;
  onClickDelete: () => void;
  onMultiTagEditClick: (e?: React.MouseEvent) => void;
  onCreateSubfolderFromSelection?: (resourceIds: string[]) => void;
  colorLabels?: ApiColorLabel[];
  resources?: ResourceDetail[];
  canModifyContent: boolean;
  spaceId?: string;
}> = ({
  colorLabels,
  onClickDelete,
  onCreateSubfolderFromSelection,
  onMultiTagEditClick,
  resources,
  onAddSelectionToSpaceClick,
  onOpenSelectionAsTabsClick,
  onAskAssistantSelectionClick,
  canModifyContent,
  spaceId,
}) => {
  const mutationChangeResourceLabel = useMutationChangeResourceLabel();
  const clearLabels = async () => {
    if (!resources || resources?.length < 1 || !spaceId) return;

    resources.forEach((resource) => {
      mutationChangeResourceLabel.mutate({
        resourceId: resource.id,
        label: null,
      });
    });
  };

  const user = useAuthStore((state) => state.user, shallow);
  const { fabricExtensionInstalled } = useOpenInTabs();

  const resourceIds = useMemo(() => {
    return resources?.map((item) => item.id) ?? [];
  }, [resources]);

  const resourcesPermission = useMemo(() => {
    return getMultipleResourcesLeaveOrDeleteActionDetails(resources || []);
  }, [resources]);

  const addToAssistantContextDisabledReason = useAddToAssistantContextDisabledReason(
    resources?.length ?? 0,
  );

  const shareToken = useContextShareToken();

  return (
    <>
      {!shareToken && (
        <>
          {onCreateSubfolderFromSelection && resources && (
            <ContextMenu.Item
              onClick={() => onCreateSubfolderFromSelection(resourceIds)}
              data-testid="create-sub-folder-and-move-context-menu"
              disabled={!canModifyContent}
              disabledReason={resourcesPermission.moveDisabledReason}
            >
              New folder from selection
            </ContextMenu.Item>
          )}
          <ContextMenu.Item onClick={onMultiTagEditClick} data-testid="edit-tags-context-menu">
            Add/edit tags
          </ContextMenu.Item>
        </>
      )}

      <ContextMenu.Group borderBottom>
        <ContextMenu.Sub>
          <ContextMenu.SubTrigger>
            <DropdownItemIconContainer>
              <IconAssistantFace />
            </DropdownItemIconContainer>
            Ask AI...
            <ContextMenu.ChevronRight />
          </ContextMenu.SubTrigger>
          <ContextMenu.Portal>
            <ContextMenu.SubContent>
              <ContextMenu.Item onClick={(e) => onAskAssistantSelectionClick(e, false)}>
                Ask about items
              </ContextMenu.Item>
              <ContextMenu.Item
                onClick={(e) => onAskAssistantSelectionClick(e, true)}
                disabled={!!addToAssistantContextDisabledReason}
                disabledReason={addToAssistantContextDisabledReason}
              >
                Add to assistant context
              </ContextMenu.Item>
            </ContextMenu.SubContent>
          </ContextMenu.Portal>
        </ContextMenu.Sub>
      </ContextMenu.Group>

      {!shareToken && (
        <ContextMenu.Item
          onClick={onAddSelectionToSpaceClick}
          data-testid="add-selection-to-space-context-menu"
          disabled={!canModifyContent}
          disabledReason={resourcesPermission.moveDisabledReason}
        >
          Move...
        </ContextMenu.Item>
      )}

      <ContextMenuCopyResources resources={resources!} />
      {fabricExtensionInstalled && (
        <ContextMenu.Item
          onClick={onOpenSelectionAsTabsClick}
          data-testid="open-selection-as-tabs-context-menu"
        >
          Open selection as tabs
        </ContextMenu.Item>
      )}
      {user?.id &&
        colorLabels &&
        resources &&
        (resources?.length ?? 0) > 0 &&
        canModifyContent &&
        !shareToken &&
        spaceId && (
          <ContextMenu.ItemRaw>
            <LabelSelector spaceId={spaceId} resources={resources} colorLabels={colorLabels} />
            {resources.find((resource) => !!resource.label) && (
              <ContextMenu.Item onClick={clearLabels} data-testid="clear-labels-context-menu">
                Clear labels
              </ContextMenu.Item>
            )}
          </ContextMenu.ItemRaw>
        )}

      {!shareToken && (
        <ContextMenu.Item
          onSelect={onClickDelete}
          data-testid="delete-selection-context-menu"
          variant="danger"
          disabledReason={resourcesPermission.deleteOrLeaveDisabledReason}
        >
          {resourcesPermission.deleteOrLeaveLabel} selection
        </ContextMenu.Item>
      )}
    </>
  );
};
